import { createFileRoute, Link } from '@tanstack/react-router';
import Hero from '../../components/Hero';
import { basicHead } from '../../utility/head';

export const Route = createFileRoute('/_marketing/privacy')({
  component: RouteComponent,
  ssr: true,
  loader: ({ location }) => ({
    pathname: location.pathname,
    seo: {
      title: 'Privacy Policy - Stark Crawl',
      description:
        'Read the Stark Crawl privacy policy to understand how we collect, use, and protect your personal data under GDPR.',
      keywords: 'Stark Crawl privacy policy, data protection, GDPR',
    },
  }),
  head: basicHead,
});

function RouteComponent() {
  return (
    <section className="py-[3.75rem] lg:py-20 px-5 border-b border-grey-t-800 grow">
      <Hero
        breadcrumb="Privacy Policy"
        title="Privacy Policy"
        pathname="/privacy"
      />
      <article className="mt-5 space-y-3 lg:mt-[3.75rem] max-w-[57.5rem] mx-auto prose prose-lg">
        <p className="text-sm text-gray-500">Last updated: October 2026</p>

        <h2 className='text-h2 font-semibold font-grotesque mt-10 mb-5'>1. Who We Are</h2>
        <p>
          Stark Crawl is a trading name of Aduro Creative Ltd, a company registered in England and Wales (Company Number: 11200639). We are the data controller responsible for your personal data.
        </p>
        <p>
          If you have any questions about this Privacy Policy or how we handle your data, please contact us at{' '}
          <a href="mailto:privacy@starkcrawl.com">privacy@starkcrawl.com</a>.
        </p>

        <h2 className='text-h2 font-semibold font-grotesque mt-10 mb-5'>2. What Data We Collect</h2>
        <p>We collect and process the following categories of personal data:</p>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>2.1 Account Information</h3>
        <ul className='list-disc ml-5 space-y-2 mt-5'>
          <li>Your name</li>
          <li>Email address</li>
        </ul>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>2.2 Content You Upload or Connect</h3>
        <p>
          When you upload files or connect third-party sources (such as Google Drive, Notion, or Dropbox), we process this content by converting it to markdown or plain text format. We then generate and store text embeddings and content chunks to enable our search and retrieval functionality.
        </p>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>2.3 Usage and Session Data</h3>
        <ul className='list-disc ml-5 space-y-2 mt-5'>
          <li>Your queries and responses within chat sessions</li>
          <li>Page views and product usage patterns</li>
          <li>
            IP address. We collect and retain your IP address for fraud
            prevention, chargeback defence, security, and as supporting evidence
            for determining the correct tax treatment of a purchase. Card issuer
            country is our primary and determinative evidence of location for tax
            purposes; IP address is treated as non-determinative, because IP
            location is unreliable (VPNs, corporate proxies, and mobile-carrier
            routing). Where collected for tax-evidence purposes, IP address is
            retained for 6 years in line with VAT record-keeping requirements
            (see Section 8).
          </li>
        </ul>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>2.4 Sharing and Access Rules</h3>
        <p>
          When you share projects, we store access rules including email addresses or email domains you have authorised to access your content.
        </p>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>2.5 Payment Information</h3>
        <p>
          Payment processing is handled by Stripe. We do not store your full payment card details on our servers. Stripe may collect and process your payment information in accordance with their own privacy policy.
        </p>

        <h2 className='text-h2 font-semibold font-grotesque mt-10 mb-5'>3. How We Use Your Data</h2>
        <p>We use your personal data for the following purposes:</p>
        <ul className='list-disc ml-5 space-y-2 mt-5'>
          <li>
            <strong>Providing our services:</strong> Processing your uploaded content, generating embeddings, and delivering AI-powered search and chat functionality
          </li>
          <li>
            <strong>Account management:</strong> Creating and managing your account, authenticating your access
          </li>
          <li>
            <strong>Sharing functionality:</strong> Enabling you to share projects with others via email-based access rules
          </li>
          <li>
            <strong>Payment processing:</strong> Processing subscription payments through Stripe
          </li>
          <li>
            <strong>Analytics and improvement:</strong> Understanding how users interact with our platform to improve our services
          </li>
          <li>
            <strong>Error monitoring:</strong> Identifying and resolving technical issues to maintain service quality
          </li>
          <li>
            <strong>Marketing measurement:</strong> Measuring the effectiveness of our advertising campaigns
          </li>
        </ul>

        <h2 className='text-h2 font-semibold font-grotesque mt-10 mb-5'>4. Legal Bases for Processing</h2>
        <p>Under the UK and EU General Data Protection Regulation (GDPR), we rely on the following legal bases:</p>
        <ul className='list-disc ml-5 space-y-2 mt-5'>
          <li>
            <strong>Contract:</strong> Processing your account information and uploaded content is necessary to provide you with our services under our Terms of Service.
          </li>
          <li>
            <strong>Legitimate interests:</strong> We process usage data and analytics based on our legitimate interest in understanding how our service is used, improving our platform, measuring advertising effectiveness, and maintaining security. This includes processing your IP address for fraud prevention, chargeback defence, and security. We have assessed that these interests do not override your fundamental rights and freedoms.
          </li>
          <li>
            <strong>Consent:</strong> For non-essential cookies on our marketing website, we obtain your consent via the cookie banner.
          </li>
          <li>
            <strong>Legal obligation:</strong> We may process and retain data where required by law, including retaining transaction records and supporting tax-location evidence (such as card issuer country and IP address) for the periods required by VAT record-keeping rules.
          </li>
        </ul>

        <h2 className='text-h2 font-semibold font-grotesque mt-10 mb-5'>5. AI Processing and Data Isolation</h2>
        <p>
          Your crawl content is processed with hosted language models for topic
          discovery and listing classify. It is important to understand:
        </p>
        <ul className='list-disc ml-5 space-y-2 mt-5'>
          <li>
            <strong>Your data is isolated:</strong> Your uploaded content and generated embeddings are stored separately and are not accessible to other users or knowledge bases outside of your authorised sharing settings.
          </li>
          <li>
            <strong>No training on your data:</strong> The AI models we use do not train on your data. Your content is used solely to provide you with responses and is not used to improve or train the underlying AI models.
          </li>
          <li>
            <strong>Session-based queries:</strong> Your chat queries and responses are session data associated only with your account.
          </li>
        </ul>

        <h2 className='text-h2 font-semibold font-grotesque mt-10 mb-5'>6. Data Sharing and Third Parties</h2>
        <p>We share your personal data with the following categories of recipients:</p>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>6.1 Infrastructure and Hosting</h3>
        <ul className='list-disc ml-5 space-y-2 mt-5'>
          <li>
            <strong>Amazon Web Services (AWS):</strong> Cloud infrastructure
            provider hosting our application, databases, stored content, and
            language-model inference (Regions: eu-west-2 London for application
            infrastructure; eu-west-1 Ireland and eu-west-2 London for inference)
          </li>
          <li>
            <strong>Pinecone:</strong> Managed vector database storing embeddings of your content for retrieval (United States)
          </li>
          <li>
            <strong>Cloudflare:</strong> DNS management for customer custom domains only — no application or customer content routes through Cloudflare
          </li>
        </ul>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>6.2 Service Providers</h3>
        <ul className='list-disc ml-5 space-y-2 mt-5'>
          <li>
            <strong>Stripe:</strong> Payment processing
          </li>
          <li>
            <strong>Mixpanel:</strong> Product analytics
          </li>
          <li>
            <strong>Google Analytics:</strong> Website analytics
          </li>
          <li>
            <strong>Sentry:</strong> Error monitoring and debugging
          </li>
          <li>
            <strong>Google Ads:</strong> Advertising and conversion tracking
          </li>
        </ul>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>6.3 Authorised Users</h3>
        <p>
          When you share a project publicly or via email access rules, the content chunks (but not original source files) become accessible to those you have authorised. You control who can access your shared projects.
        </p>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>6.4 Legal Requirements</h3>
        <p>
          We may disclose your data if required by law, regulation, legal process, or governmental request.
        </p>

        <h2 className='text-h2 font-semibold font-grotesque mt-10 mb-5'>7. International Data Transfers</h2>
        <p>
          Your data is primarily stored and processed within the United Kingdom and European Economic Area (EEA). Our primary infrastructure is located in:
        </p>
        <ul className='list-disc ml-5 space-y-2 mt-5'>
          <li>AWS eu-west-2 (London, UK) — application, databases, storage, and authentication</li>
          <li>AWS eu-west-1 (Ireland) and eu-west-2 (London) — language-model inference</li>
        </ul>
        <p>
          Some of our third-party providers (including Pinecone, Stripe, Sentry, Mixpanel, Google Analytics, Google Ads, Cloudflare, and Firecrawl) may transfer data to the United States. Where such transfers occur, they are protected by appropriate safeguards, including Standard Contractual Clauses (SCCs) approved by the European Commission, the UK International Data Transfer Addendum, and — where the recipient is currently certified — the EU–US Data Privacy Framework.
        </p>

        <h2 className='text-h2 font-semibold font-grotesque mt-10 mb-5'>8. Data Retention</h2>
        <p>We retain your personal data as follows:</p>
        <ul className='list-disc ml-5 space-y-2 mt-5'>
          <li>
            <strong>Account data:</strong> Retained for as long as your account is active
          </li>
          <li>
            <strong>Uploaded content and embeddings:</strong> Retained for as long as your account is active
          </li>
          <li>
            <strong>After account deletion:</strong> We delete your data within 90 days of account closure, unless we are required to retain it for longer to comply with legal obligations, resolve disputes, or enforce our agreements
          </li>
          <li>
            <strong>Analytics data:</strong> Retained in accordance with our analytics providers&apos; retention policies
          </li>
          <li>
            <strong>Transaction and tax records:</strong> Records of your purchases, together with the supporting tax-location evidence (including card issuer country, declared country, VAT number and validation result, billing address, and IP address), are retained for 6 years to meet VAT record-keeping requirements, even after account closure.
          </li>
        </ul>

        <h2 className='text-h2 font-semibold font-grotesque mt-10 mb-5'>9. Your Rights</h2>
        <p>
          Under GDPR, you have the following rights regarding your personal data:
        </p>
        <ul className='list-disc ml-5 space-y-2 mt-5'>
          <li>
            <strong>Right of access:</strong> You can request a copy of the personal data we hold about you.
          </li>
          <li>
            <strong>Right to rectification:</strong> You can request that we correct any inaccurate or incomplete data.
          </li>
          <li>
            <strong>Right to erasure:</strong> You can request that we delete your personal data in certain circumstances.
          </li>
          <li>
            <strong>Right to restrict processing:</strong> You can request that we limit how we use your data.
          </li>
          <li>
            <strong>Right to data portability:</strong> You can request a copy of your data in a structured, machine-readable format.
          </li>
          <li>
            <strong>Right to object:</strong> You can object to processing based on legitimate interests, including for direct marketing purposes.
          </li>
          <li>
            <strong>Right to withdraw consent:</strong> Where processing is based on consent, you can withdraw it at any time.
          </li>
        </ul>
        <p>
          To exercise any of these rights, please contact us at{' '}
          <a href="mailto:privacy@starkcrawl.com">privacy@starkcrawl.com</a>. We will respond to your request within one month.
        </p>
        <p>
          You also have the right to lodge a complaint with a supervisory authority. In the UK, this is the Information Commissioner&apos;s Office (ICO) at{' '}
          <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">
            ico.org.uk
          </a>
          .
        </p>

        <h2 className='text-h2 font-semibold font-grotesque mt-10 mb-5'>10. Cookies and Tracking</h2>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>10.1 Essential Cookies</h3>
        <p>
          We use essential cookies that are strictly necessary for the operation of our platform, including authentication and session management. These do not require consent.
        </p>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>10.2 Analytics Cookies</h3>
        <p>
          We use Mixpanel and Google Analytics to understand how users interact with our platform. These tools collect information about page views, feature usage, and user journeys. This data is linked to your user account to help us improve our services.
        </p>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>10.3 Advertising Cookies</h3>
        <p>
          We use Google Ads conversion tracking (including GCLID) to measure the effectiveness of our advertising campaigns. On our marketing website, consent for advertising cookies is managed through the cookie banner.
        </p>

        <h3 className='mt-5 mb-4 text-h3 font-semibold'>10.4 Managing Cookies</h3>
        <p>
          You can manage your cookie preferences through the cookie banner on our marketing website, or reopen it from Cookie settings in the footer. You can also configure your browser to refuse cookies, although this may affect the functionality of our services.
        </p>

        <h2 className='text-h2 font-semibold font-grotesque mt-10 mb-5'>11. Security</h2>
        <p>
          We implement appropriate technical and organisational measures to protect your personal data against unauthorised access, alteration, disclosure, or destruction. These measures include encryption in transit and at rest, access controls, and regular security assessments.
        </p>
        <p>
          However, no method of transmission over the internet or electronic storage is completely secure. While we strive to protect your data, we cannot guarantee absolute security.
        </p>

        <h2 className='text-h2 font-semibold font-grotesque mt-10 mb-5'>12. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of any material changes by posting the updated policy on our website and updating the &quot;Last updated&quot; date. We encourage you to review this policy periodically.
        </p>

        <h2 className='text-h2 font-semibold font-grotesque mt-10 mb-5'>13. Contact Us</h2>
        <p>
          If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
        </p>
        <p>
          <strong>Email:</strong>{' '}
          <a href="mailto:privacy@starkcrawl.com">privacy@starkcrawl.com</a>
        </p>
        <p>
          <strong>Company:</strong> Aduro Creative Ltd (trading as Stark Crawl)
          <br />
          Company Number: 11200639
        </p>
        <p>
          <strong>Address:</strong> Aduro Creative Ltd (trading as Stark Crawl)
          <br />
          27 Old Gloucester Street, London, WC1N 3AX
        </p>

        <h2 className='text-h2 font-semibold font-grotesque mt-10 mb-5'>Addendum: Data Processing Agreement</h2>
        <p>
          Where Stark Crawl processes Personal Data on your behalf as a processor (for example, content you upload or connect through third-party integrations), the terms of our{' '}
          <Link to="/data-processing-agreement" className="underline">Data Processing Agreement</Link>{' '}
          apply in addition to this Privacy Policy and form an integral part of it. The DPA sets out the parties&apos; respective obligations under Article 28 of the UK GDPR and references the current list of authorised{' '}
          <Link to="/subprocessors" className="underline">Sub-processors</Link>.
        </p>
      </article>
    </section>
  );
}